import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Table, Tooltip } from 'antd';
import { get, includes, slice, some, size } from 'lodash';
import { _get, cleanOrderId, calculateBillingDeadline } from '../../../utils/misc';
import AssignPicker from '../../graphql/assignPicker';
import OrderPickerManager from './OrderPickerManager';
import {
    HasPermissions,
    Number,
    Icon,
    Row,
    Column,
    FlexContainer,
    Text,
    StatusTag,
    Link,
    Truncate,
    TableContainer,
    OrderShippingEstimate,
    IsMobileWrapper,
    If,
    Tag
} from '../../common';
import { IconCencoPrime } from './../../common/Icon/IconCencoPrime';
import { TIGERS_EYE, GREEN_DARK, MAHOGANY, APPLE_GREEN } from '../../../utils/colors';
import { getStatusCode } from '../../../utils/misc';

const pendingPickingValues = getStatusCode('Pendiente picking');
const enPickingValues = getStatusCode('En picking');

const ShippingCountdownContainer = styled.div`
    background-color: ${({ backgroundColor = '#ffffff' }) => backgroundColor};
    padding: 3px 15px;
    border-radius: 4px;
    text-align: center;
    width: fit-content;
    margin: auto;
`;

const getIconTypeClient = (item) => {
    if (get(item, 'isUltraVIP')) return <Icon name='ultravip' size={20} right={5} color='#1C8AF5' />;
    if (get(item, 'isVIP')) return <Icon name='vip' size={20} right={5} color='#4DB457' />;
    if (get(item, 'isCencoPrime')) return <IconCencoPrime />;
    return '';
};

const getInitialColumns = (session) => [
    {
        title: 'Id Pedido',
        dataIndex: 'id',
        key: 'name',
        className: 'bo-table-header',
        width: 130,
        fixed: 'left',
        render: (value, item) => {
            const { id, windowAnticipated = false, shippingChange, extIssueDelayedByQueuing, extIssueIsDelayed, isPersonalShopperCustomer } = item;
            const isQueued = extIssueDelayedByQueuing > 0;
            const isAnticipated = windowAnticipated && !shippingChange;
            const showExternalFactor = (extIssueIsDelayed && isAnticipated && isQueued) ? false : extIssueIsDelayed;

            return <HasPermissions
                to={['orders-page:order-detail']}
                otherwise={<Text text={cleanOrderId(value)} size={11} />}
            >
                <Row>
                    {isQueued && <Tooltip title='Encolamiento pedido'>
                        <div><Icon name='queuing' size={18} right={3} color={MAHOGANY} /></div>
                    </Tooltip>}
                    {showExternalFactor && <Tooltip title='Factores externos'>
                        <div><Icon name='external-factors' size={18} right={3} color='#046017' /></div>
                    </Tooltip>}
                    {isAnticipated && <Icon name='clock-fast' size={18} right={3} color={TIGERS_EYE} />}
                    {isPersonalShopperCustomer && <Icon name='human-star' size={18} right={3} color='#C65006' />}
                    <Truncate style={{ margin: 'auto 0px' }}>
                        <Link text={cleanOrderId(value)} src={`orders/${id}`} />
                    </Truncate>
                </Row>
            </HasPermissions>;
        }
    },
    {
        title: 'Tipo Pedido',
        key: 'split',
        width: 113,
        align: 'center',
        className: 'bo-table-header',
        render: (value) => {
            const isSplit = get(value, 'split');
            const isBipicking = size(get(value, 'ordersBipicking', [])) > 0;

            let tagType = 'monopicking-order';
            let text = 'Monopicking';

            if (isSplit) {
                tagType = 'split-order';
                text = 'Split order';
            } else if (isBipicking) {
                tagType = 'bipicking-order';
                text = 'Bipicking';
            }

            return <Tag tagType={tagType}>
                <Text text={text} semibold size={11} />
            </Tag>;
        }
    },
    {
        title: 'Fecha entrega',
        dataIndex: 'logisticsInfo',
        // defaultSortOrder: 'descend',
        // sorter: (a, b) => new Date(b.logisticsInfo.shippingEstimateDate) - new Date(a.logisticsInfo.shippingEstimateDate),
        sortDirections: ['ascend', 'descend'],
        key: 'shippingEstimateDate',
        className: 'bo-table-header',
        width: 100,
        align: 'center',
        render: (dataIndex, item, index) => {
            const shippingChange = get(item, 'shippingChange', false);
            return (
                <div>
                    <OrderShippingEstimate {...item}>
                        {({ deliveryDate }) => <Text text={deliveryDate} strong={shippingChange} color={shippingChange ? '#5665f5' : '#333333'} />}
                    </OrderShippingEstimate>
                </div>
            );
        }
    },
    {
        title: 'Tiempo faltante',
        className: 'bo-table-header',
        width: 145,
        align: 'center',
        render: (value, item) => {
            if (includes([34, 41], parseInt(get(item, 'status', get(item, 'pickingInfo.status.code', 0))))) {
                return (
                    <FlexContainer justify='center'>
                        <Text text='-' />
                    </FlexContainer>
                );
            }
            return (
                <OrderShippingEstimate {...item} withInterval={!includes([5, 41], parseInt(get(item, 'pickingInfo.status.code', 0)))}>
                    {({ shippingCountdown, invoicedInTime }) => {
                        const { label, colors } = shippingCountdown;
                        if (invoicedInTime) {
                            return (
                                <ShippingCountdownContainer backgroundColor='#5665f5'>
                                    <Text text='En plazo' color='#ffffff' semibold />
                                </ShippingCountdownContainer>
                            );
                        }
                        return (
                            <ShippingCountdownContainer backgroundColor={get(colors, 'hex')}>
                                <Text text={label} color={get(colors, 'text')} semibold />
                            </ShippingCountdownContainer>
                        );
                    }}
                </OrderShippingEstimate>
            );
        }
    },
    {
        title: 'Límite facturación',
        dataIndex: 'logisticsInfo.deliveryAverage',
        // defaultSortOrder: 'descend',
        width: 131,
        align: 'center',
        sorter: (a, b) => new Date(b.logisticsInfo.deliveryAverage) - new Date(a.logisticsInfo.deliveryAverage),
        sortDirections: ['ascend', 'descend'],
        key: 'deliveryAverage',
        className: 'bo-table-header',
        render: (dataIndex, item, index) => {
            const isExpress = get(item, 'logisticsInfo.selectedSla', null) === 'Despacho Express';
            const shippingChange = get(item, 'shippingChange', false);
            const windowAnticipated = get(item, 'windowAnticipated', false)
            const isAnticipated = windowAnticipated && !shippingChange;
            const category = get(item, 'category');
            const time = get(item, 'time');
            const startDateUtc = get(item, 'logisticsInfo.deliveryWindow.startDateUtc');
            const endDateUtc = get(item, 'logisticsInfo.deliveryWindow.endDateUtc');
            const init = startDateUtc === 'Invalid date' ? get(item, 'logisticsInfo.shippingETA') : startDateUtc;
            const end = endDateUtc === 'Invalid date' ? get(item, 'logisticsInfo.shippingETAend') : endDateUtc;

            if (isExpress) {
                return (
                    <OrderShippingEstimate {...item}>
                        {({ deliveryTimeHourBySla }) => <Text text={deliveryTimeHourBySla} strong={shippingChange} color={shippingChange ? '#5665f5' : '#333333'} />}
                    </OrderShippingEstimate>
                );
            }
            const newDate = calculateBillingDeadline(category, time, init, end, isAnticipated).format('HH:mm');
            return <Text text={`${newDate} hrs.`} strong={shippingChange} color={shippingChange ? '#5665f5' : '#333333'} />;
        }
    },
    {
        title: 'Estado',
        dataIndex: 'pickingInfo.status',
        key: 'status',
        className: 'bo-table-header',
        width: 186,
        align: 'center',
        render: (item, dataIndex) => (
            <FlexContainer justify='center'>
                <StatusTag
                    dot
                    messageIndex={0}
                    tagType={_get(item, 'label', 'pending')}
                    status={`order_status.${_get(item, 'label', 'pending')}`}
                    mL={0}
                />
            </FlexContainer>)
    },
    {
        title: 'Shopper',
        className: 'bo-table-header',
        dataIndex: 'salesChannel.ido',
        key: 'salesChannel.id',
        width: 200,
        align: 'center',
        render: (dataIndex, item) => {
            const hasPermissionForThisStore = some(get(session, 'salesChannels'), { id: get(item, 'salesChannelId', '') });

            return (
                <IsMobileWrapper>
                    {({ isMobile }) => (
                        <AssignPicker>
                            {({ updateFn, loading }) => (
                                <OrderPickerManager
                                    assignDisabled={isMobile}
                                    hasPermissionToThisStore={hasPermissionForThisStore}
                                    salesChannelId={get(item, 'salesChannelId', null)}
                                    permissions={['orders-page:assign-picker']}
                                    filters={{
                                        filters: [
                                            { field: 'salesChannels', value: [get(item, 'salesChannelId', '1')] },
                                            { field: 'statusUser', value: ['0'] }
                                        ]
                                    }}
                                    onSelectPicker={({ value }) => Promise.resolve(
                                        updateFn({
                                            variables: {
                                                input: { orderId: get(item, 'id'), userId: value }
                                            }
                                        })
                                    )}
                                    loading={loading}
                                    {...item}
                                    session={session}
                                />
                            )}
                        </AssignPicker>
                    )}
                </IsMobileWrapper>
            );
        }
    },
    {
        title: 'Tipo despacho',
        dataIndex: 'logisticsInfo.selectedSla',
        key: 'logisticsInfo.selectedSla',
        className: 'bo-table-header',
        width: 164,
        align: 'center',
        render: (value, item) => {
            return <Text text={value} />;
        }
    },
    {
        title: 'Comuna',
        dataIndex: 'selectedAddress.neighborhood',
        key: 'neighborhood',
        className: 'bo-table-header',
        width: 150,
        align: 'center'
    },
    {
        title: 'Cliente',
        dataIndex: 'customer',
        key: 'customer-info',
        className: 'bo-table-header',
        width: 190,
        align: 'center',
        render: (item) => {
            return <Row style={{ flexWrap: 'nowrap' }}>
                <Column maxWidth={25}>
                    {getIconTypeClient(item)}
                </Column>
                <Column align='center'>
                    <Truncate width={120}>
                        <Text text={get(item, 'fullName')} />
                    </Truncate> 
                </Column>
            </Row>;
        }
    },
    {
        title: 'SKU',
        dataIndex: 'sku',
        key: 'sku',
        className: 'bo-table-header align-center',
        width: 70,
        align: 'center',
        render: (value, item) => {
            const status = get(item, 'pickingInfo.status.code', '0'); 
            
            return (
                (pendingPickingValues.includes(String(status)) || enPickingValues.includes(String(status))) ?
                <HasPermissions to={['orders-page:view-sku']} otherwise="-">
                    <Text text={`${get(item, 'skuCount', 0)}`} />
                </HasPermissions>
            :
                <Text text={`${get(item, 'skuCount', 0)}`} />
            );
        }
    },
    {
        title: 'UNI',
        dataIndex: 'uni',
        key: 'uni',
        className: 'bo-table-header align-center',
        width: 70,
        align: 'center',
        render: (value, item) => {
            const status = get(item, 'pickingInfo.status.code', '0'); 

            return (
                (pendingPickingValues.includes(String(status)) || enPickingValues.includes(String(status))) ?
                <HasPermissions to={['orders-page:view-units']} otherwise="-">
                    <Text text={`${get(item, 'itemCount', 0)}`} />
                </HasPermissions>
            :
                <Text text={`${get(item, 'itemCount', 0)}`} />
            );
            
        }
    },
    {
        title: 'Monto del pedido',
        dataIndex: 'paymentData.transactions.0.payments.0.amount',
        key: 'reservedAmount',
        className: 'bo-table-header align-right',
        width: 125,
        align: 'center',
        render: (dataIndex, item) => {
            const status = get(item, 'pickingInfo.status.code', '0'); 

            return (
                (pendingPickingValues.includes(String(status)) || enPickingValues.includes(String(status))) ?
                <HasPermissions to={['orders-page:view-amount']} otherwise="-">
                    <Text align='right' text={<Number isPrice value={dataIndex} />} />
                </HasPermissions>
            :
                <Text align='right' text={<Number isPrice value={dataIndex} />} />
            );

        }
    },
    {
        title: 'Tienda',
        dataIndex: 'storeName',
        key: 'salesChannel.name',
        className: 'bo-table-header',
        width: 200,
        align: 'center',
        render: (value, item) => {
            return (
                <Text text={value} />
            );
        }
    }
];

const OrdersTable = ({ loading, data, networkStatus, rowSelection, session, ...props }) => (
    <TableContainer
        {...props}
        style={{ overflow: 'hidden', overflowY: 'auto' }}
    >
        {({ onRow }) => (
            <>
                <IsMobileWrapper>
                    {({ isMobile }) => {
                        const initialColumns = getInitialColumns(session);
                        const columns = isMobile ? [initialColumns[0], ...slice(initialColumns, 2, 4), ...slice(initialColumns, 5, 7), initialColumns[13]] : initialColumns;
                        return (
                            <Table
                                loading={loading}
                                className='bo-table order-table orders-table'
                                size='small'
                                columns={columns}
                                dataSource={data}
                                pagination={false}
                                rowKey={record => record.id}
                                scroll={{ y: 'calc(100vh - 265px)', x: isMobile ? '100%' : 2220 }}
                                rowSelection={rowSelection}
                            />
                        );
                    }}
                </IsMobileWrapper>
            </>
        )}
    </TableContainer>
);

OrdersTable.propTypes = {
    loading: PropTypes.bool,
    data: PropTypes.array,
    pageSize: PropTypes.number,
    pages: PropTypes.number,
    networkStatus: PropTypes.number,
    rowSelection: PropTypes.object,
    session: PropTypes.object
};

OrdersTable.defaultProps = {
    pageSize: 5,
    pages: 1,
    rowSelection: {}
};

export default OrdersTable;